/**
 * API client for making authenticated requests to the Cloudflare Workers API
 */

// Determine API base URL based on environment
// Production domains use the production API, development uses localhost
function getApiBaseUrl(): string {
  // First check if explicitly set via environment variable
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  // Auto-detect based on hostname
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  // Production domains (SWAK + legacy lipstick domains)
  if (
    hostname === 'app.sealedkiss.com' ||
    hostname === 'admin.sealedkiss.com' ||
    hostname === 'sealedkiss.com' ||
    hostname === 'www.sealedkiss.com'
  ) {
    return 'https://api.sealedkiss.com';
  }
  // Legacy lipstick.studio domains route to the live API. The old
  // api.lipstick.studio host is decommissioned — never point here.
  if (
    hostname === 'app.lipstick.studio' ||
    hostname === 'lipstick.studio' ||
    hostname === 'www.lipstick.studio'
  ) {
    return 'https://api.sealedkiss.com';
  }

  // Cloudflare Pages preview deployments
  if (hostname.endsWith('.pages.dev') || hostname.endsWith('.workers.dev')) {
    return '';
  }

  // Default to localhost for development
  return 'http://localhost:8787';
}

export const API_BASE_URL = getApiBaseUrl();
const REQUEST_TIMEOUT_MS = 10000;
const RETRY_BACKOFF_MS = 300;
const MAX_RETRY_ATTEMPTS = 2;
const RETRYABLE_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504]);

export class ApiRequestError extends Error {
  status: number;
  data: any;

  constructor(message: string, status: number, data: any) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.data = data;
  }
}

function isIdempotentMethod(method: string) {
  return method === 'GET' || method === 'HEAD' || method === 'OPTIONS';
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchWithTimeout(url: string, options: RequestInit, timeoutMs: number) {
  if (options.signal) {
    return fetch(url, options);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiRequestError(`Request timed out after ${timeoutMs}ms`, 408, { timeout: true });
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

type ApiTenantContext = {
  id: string | null;
  slug: string | null;
};

let currentTenantContext: ApiTenantContext = { id: null, slug: null };

/**
 * Set the tenant context for all subsequent API requests.
 */
export function setApiTenant(tenant: ApiTenantContext | string | null) {
  if (typeof tenant === 'string') {
    currentTenantContext = { id: null, slug: tenant };
    return;
  }

  currentTenantContext = {
    id: tenant?.id ?? null,
    slug: tenant?.slug ?? null,
  };
}

/**
 * Get the current tenant context
 */
export function getApiTenant() {
  return currentTenantContext;
}

function applyTenantHeaders(headers: Record<string, string>) {
  const hasTenantIdHeader = Boolean(headers['X-Tenant-ID'] || headers['x-tenant-id']);
  const hasTenantSlugHeader = Boolean(headers['X-Tenant-Slug'] || headers['x-tenant-slug']);

  if (currentTenantContext.id && !hasTenantIdHeader) {
    headers['X-Tenant-ID'] = currentTenantContext.id;
  }

  if (currentTenantContext.slug && !hasTenantSlugHeader) {
    headers['X-Tenant-Slug'] = currentTenantContext.slug;
  }
}

interface RequestOptions extends RequestInit {
  requireAuth?: boolean;
}

/**
 * Make an authenticated API request
 * @param endpoint - API endpoint (e.g., '/api/catalog/portfolio-items')
 * @param options - Fetch options plus optional requireAuth flag
 * @param getToken - Function to get the current user's JWT token
 */
export async function apiRequest<T = any>(
  endpoint: string,
  options: RequestOptions = {},
  getToken?: () => Promise<string | null>
): Promise<T> {
  if (!API_BASE_URL) {
    throw new Error('API base URL is not configured. Set VITE_API_BASE_URL for this environment.');
  }

  const { requireAuth = false, headers = {}, ...fetchOptions } = options;

  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers as Record<string, string>,
  };

  // Add authorization header if authentication is required
  if (requireAuth && !getToken) {
    throw new Error('Authenticated request is missing token provider');
  }

  if (requireAuth && getToken) {
    const token = await getToken();
    if (!token) {
      throw new Error('Authentication required but no token available');
    }
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }

  applyTenantHeaders(requestHeaders);

  const url = `${API_BASE_URL}${endpoint}`;
  const method = (fetchOptions.method || 'GET').toUpperCase();
  const canRetry = isIdempotentMethod(method);
  const maxAttempts = canRetry ? MAX_RETRY_ATTEMPTS + 1 : 1;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const response = await fetchWithTimeout(
        url,
        {
          ...fetchOptions,
          method,
          headers: requestHeaders,
        },
        REQUEST_TIMEOUT_MS
      );

      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      const isJson = contentType?.includes('application/json');

      if (!response.ok) {
        if (canRetry && RETRYABLE_STATUS_CODES.has(response.status) && attempt < maxAttempts - 1) {
          await sleep(RETRY_BACKOFF_MS * (attempt + 1));
          continue;
        }

        const error = isJson ? await response.json() : { message: await response.text() };
        const baseMessage = (error && (error.message || error.error)) || `API error: ${response.status}`;
        const message = error?.details ? `${baseMessage}: ${error.details}` : baseMessage;
        throw new ApiRequestError(message as string, response.status, error);
      }

      return isJson ? await response.json() : (await response.text()) as T;
    } catch (error) {
      const isRetryableNetworkError =
        error instanceof TypeError ||
        (error instanceof ApiRequestError && RETRYABLE_STATUS_CODES.has(error.status));

      if (canRetry && isRetryableNetworkError && attempt < maxAttempts - 1) {
        await sleep(RETRY_BACKOFF_MS * (attempt + 1));
        continue;
      }

      console.error('API request failed:', error);
      throw error;
    }
  }

  throw new ApiRequestError('API request failed after retries', 500, null);
}

export type WebOptimizerPageKey = 'home' | 'about' | 'storefront' | 'contact' | 'bookings' | 'location';

export interface WebOptimizerSuggestion {
  field: 'title' | 'subtitle' | 'body_md' | 'meta_title' | 'meta_description' | 'meta_keywords' | 'cta_text';
  value: string;
  rationale: string;
}

export interface WebOptimizerMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  suggestions?: WebOptimizerSuggestion[];
  status: 'pending' | 'accepted' | 'discarded';
  createdAt: string;
}

export interface WebOptimizerSession {
  sessionId: string;
  tenantId?: string;
  pageKey?: WebOptimizerPageKey;
  messages: WebOptimizerMessage[];
  updatedAt?: string;
}

export interface WebOptimizerResponse extends WebOptimizerSession {
  runId: string;
  message: string;
  suggestions: WebOptimizerSuggestion[];
  gatewayLogId?: string;
  modelUsed: string;
  promptVersion: string;
}

export const webOptimizerApi = {
  getSession(pageKey: WebOptimizerPageKey, getToken: () => Promise<string | null>) {
    return apiRequest<WebOptimizerSession>(
      `/api/ai/web-optimizer/sessions/${pageKey}`,
      { requireAuth: true },
      getToken,
    );
  },

  sendMessage(
    pageKey: WebOptimizerPageKey,
    data: {
      currentContent: Record<string, unknown>;
      prompt: string;
      tone?: string;
    },
    getToken: () => Promise<string | null>,
  ) {
    return apiRequest<WebOptimizerResponse>(
      `/api/ai/web-optimizer/sessions/${pageKey}/messages`,
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken,
    );
  },

  recordFeedback(
    pageKey: WebOptimizerPageKey,
    messageId: string,
    status: 'accepted' | 'discarded',
    getToken: () => Promise<string | null>,
  ) {
    return apiRequest<WebOptimizerSession>(
      `/api/ai/web-optimizer/sessions/${pageKey}/feedback`,
      {
        method: 'POST',
        body: JSON.stringify({ messageId, status }),
        requireAuth: true,
      },
      getToken,
    );
  },
};

// ── AI credit balance + image generation ────────────────────────────────────

export interface AiCreditPool {
  used: number;
  limit: number;
  remaining: number;
}

export interface AiCreditBalance {
  planTier: 'free' | 'pro' | 'enterprise';
  text: AiCreditPool;
  image: AiCreditPool;
  periodStart: string;
}

export const aiCreditsApi = {
  getBalance(getToken: () => Promise<string | null>) {
    return apiRequest<AiCreditBalance>('/api/ai/credits', { requireAuth: true }, getToken);
  },

  generateImage(
    body: { intent: 'hero' | 'about' | 'gallery'; pageKey?: string; styleHint?: string },
    getToken: () => Promise<string | null>,
  ) {
    return apiRequest<{ url: string; key: string; credits: AiCreditBalance['image'] & { planTier: string } }>(
      '/api/ai/images',
      { method: 'POST', body: JSON.stringify(body), requireAuth: true },
      getToken,
    );
  },
};

/**
 * Catalog API methods
 */
export const catalogApi = {
  /**
   * Get all published portfolio items
   */
  getPortfolioItems: async (getToken: () => Promise<string | null>) => {
    return apiRequest('/api/portfolio/admin/all', { requireAuth: true }, getToken);
  },

  /**
   * Create a new portfolio item (admin/editor only)
   */
  createPortfolioItem: async (
    data: {
      title: string;
      slug: string;
      description?: string;
      images?: string[];
      is_featured?: boolean;
      is_published?: boolean;
      display_order?: number;
    },
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      '/api/portfolio',
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Update a portfolio item (admin/editor only)
   */
  updatePortfolioItem: async (
    id: string,
    data: Partial<{
      title: string;
      slug: string;
      description: string;
      images: string[];
      is_featured: boolean;
      is_published: boolean;
      display_order: number;
    }>,
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      `/api/portfolio/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Delete a portfolio item (admin only)
   */
  deletePortfolioItem: async (
    id: string,
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      `/api/portfolio/${id}`,
      {
        method: 'DELETE',
        requireAuth: true,
      },
      getToken
    );
  },
};

/**
 * Images API methods
 */
export const imagesApi = {
  /**
   * List all images (admin/editor only)
   */
  listImages: async (
    params: { page?: number; limit?: number; search?: string; tag?: string } = {},
    getToken: () => Promise<string | null>
  ) => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.search) query.set('search', params.search);
    if (params.tag) query.set('tag', params.tag);
    const qs = query.toString();
    return apiRequest(
      `/api/images/library${qs ? `?${qs}` : ''}`,
      { requireAuth: true },
      getToken
    );
  },

  /**
   * Upload an image (admin/editor only)
   */
  uploadImage: async (
    file: File,
    getToken: () => Promise<string | null>,
    projectId?: string | null
  ) => {
    const token = await getToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    const formData = new FormData();
    formData.append('file', file);
    if (projectId) formData.append('project_id', projectId);

    const response = await fetch(`${API_BASE_URL}/api/images/upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        ...(currentTenantContext.id ? { 'X-Tenant-ID': currentTenantContext.id } : {}),
        ...(currentTenantContext.slug ? { 'X-Tenant-Slug': currentTenantContext.slug } : {}),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Image upload failed');
    }

    return response.json();
  },

  /**
   * Upload image from URL (admin/editor only)
   */
  uploadFromUrl: async (
    url: string,
    getToken: () => Promise<string | null>,
    projectId?: string | null
  ) => {
    return apiRequest(
      '/api/images/upload-from-url',
      {
        method: 'POST',
        body: JSON.stringify({ url, project_id: projectId }),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Delete an image (admin/editor only)
   */
  deleteImage: async (
    imageId: string,
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      `/api/images/${imageId}`,
      {
        method: 'DELETE',
        requireAuth: true,
      },
      getToken
    );
  },
};

/**
 * Payment API methods
 */
export const paymentApi = {
  /**
   * Create a payment intent for checkout (authenticated users)
   */
  createPaymentIntent: async (
    amount: number,
    currency: string = 'usd',
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      '/api/payments/create-intent',
      {
        method: 'POST',
        body: JSON.stringify({ amount, currency }),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Create a payment intent for immediate in-person payments (admin only)
   */
  createQuickPaymentIntent: async (
    data: {
      amount: number;
      description?: string;
      currency?: string;
      customer_id?: string;
      customer_email?: string;
      customer_name?: string;
    },
    getToken: () => Promise<string | null>
  ): Promise<{ clientSecret: string; paymentIntentId: string }> => {
    return apiRequest(
      '/api/payments/quick-intent',
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Create a payment intent for guest checkout (no auth required)
   */
  createGuestPaymentIntent: async (
    amount: number,
    email: string,
    currency: string = 'usd'
  ) => {
    return apiRequest('/api/payments/guest-intent', {
      method: 'POST',
      body: JSON.stringify({ amount, currency, email }),
    });
  },

  /**
   * Create a hosted Stripe Payment Link for in-person/event payments (admin only)
   */
  createQuickPaymentLink: async (
    data: {
      amount: number;
      description?: string;
      currency?: string;
      event_id?: string;
      event_guest_id?: string;
      customer_id?: string;
      customer_email?: string;
      customer_name?: string;
      idempotency_key?: string;
    },
    getToken: () => Promise<string | null>
  ): Promise<{ id: string; url: string; active: boolean; qr_payload: string }> => {
    return apiRequest(
      '/api/payments/quick-link',
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },
};

/**
 * Auth API methods for role management
 */
export const authApi = {
  /**
   * Get current user's roles
   */
  getUserRoles: async (getToken: () => Promise<string | null>) => {
    return apiRequest('/api/auth/roles', { requireAuth: true }, getToken);
  },

  /**
   * Sync user after sign-in (assigns default roles)
   */
  syncUser: async (getToken: () => Promise<string | null>) => {
    return apiRequest(
      '/api/auth/sync',
      {
        method: 'POST',
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Check if user has specific roles
   */
  checkRole: async (roles: string[], getToken: () => Promise<string | null>) => {
    const rolesParam = roles.join(',');
    return apiRequest(
      `/api/auth/check-role?roles=${encodeURIComponent(rolesParam)}`,
      { requireAuth: true },
      getToken
    );
  },

  /**
   * Get all available roles (admin only)
   */
  getAllRoles: async (getToken: () => Promise<string | null>) => {
    return apiRequest('/api/auth/roles/all', { requireAuth: true }, getToken);
  },

  /**
   * Assign role to user (admin only)
   */
  assignRole: async (
    data: { user_id: string; role_id: string; expires_at?: string },
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      '/api/auth/roles/assign',
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Revoke role from user (admin only)
   */
  revokeRole: async (
    data: { user_id: string; role_id: string },
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      '/api/auth/roles/revoke',
      {
        method: 'DELETE',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },
};

/**
 * Customers API methods
 */
export const customersApi = {
  /**
   * Sync customer from Neon Auth / Stack Auth
   */
  syncCustomer: async (
    data: {
      email: string;
      full_name?: string;
      phone?: string;
    },
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      '/api/customers/sync',
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Get current user's profile
   */
  getMyProfile: async (getToken: () => Promise<string | null>) => {
    return apiRequest('/api/customers/me', { requireAuth: true }, getToken);
  },

  /**
   * Update current user's profile
   */
  updateMyProfile: async (
    data: { email?: string; full_name?: string; phone?: string },
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      '/api/customers/me',
      {
        method: 'PUT',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Create a customer record (admin only)
   */
  createCustomer: async (
    data: { email?: string; full_name: string; phone?: string; notes?: string },
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      '/api/customers',
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Get all customers (admin only)
   */
  getCustomers: async (
    params: { page?: number; limit?: number; search?: string } = {},
    getToken: () => Promise<string | null>
  ) => {
    const query = new URLSearchParams();
    if (params.page) query.append('page', params.page.toString());
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.search) query.append('search', params.search);

    const queryString = query.toString();
    const url = `/api/customers${queryString ? `?${queryString}` : ''}`;

    return apiRequest(url, { requireAuth: true }, getToken);
  },

  /**
   * Get a specific customer by ID (admin only)
   */
  getCustomer: async (id: string, getToken: () => Promise<string | null>) => {
    return apiRequest(`/api/customers/${id}`, { requireAuth: true }, getToken);
  },

  /**
   * Import a Stripe-originated customer into the tenant customer list.
   */
  importStripeCustomer: async (
    data: { stripe_customer_id?: string; email?: string },
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      '/api/customers/import-stripe',
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Bulk-import all customers from the tenant's connected Stripe account.
   */
  syncFromStripe: async (getToken: () => Promise<string | null>) => {
    return apiRequest(
      '/api/customers/sync-from-stripe',
      { method: 'POST', requireAuth: true },
      getToken
    ) as Promise<{ synced: number; created: number; updated: number }>;
  },

  /**
   * Ensure a tenant-scoped Stripe customer exists for the given customer.
   */
  reconcileStripeCustomer: async (
    id: string,
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      `/api/customers/${id}/reconcile-stripe`,
      {
        method: 'POST',
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * List customer purchase images (admin only)
   */
  getCustomerPurchaseImages: async (id: string, getToken: () => Promise<string | null>) => {
    return apiRequest(`/api/customers/${id}/purchase-images`, { requireAuth: true }, getToken);
  },

  /**
   * Upload customer purchase image (admin only)
   */
  uploadCustomerPurchaseImage: async (
    id: string,
    data: { file: File; product_name?: string; notes?: string },
    getToken: () => Promise<string | null>
  ) => {
    const token = await getToken();
    const formData = new FormData();
    formData.append('file', data.file);
    if (data.product_name) formData.append('product_name', data.product_name);
    if (data.notes) formData.append('notes', data.notes);

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };
    applyTenantHeaders(headers);

    const response = await fetch(`${API_BASE_URL}/api/customers/${id}/purchase-images`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || error.message || 'Upload failed');
    }

    return response.json();
  },

  /**
   * Create customer quick payment intent (admin only).
   * Pass appointment_id / service_id to link the payment to a booking.
   */
  createCustomerPaymentIntent: async (
    id: string,
    data: {
      amount: number;
      currency?: string;
      description?: string;
      appointment_id?: string;
      service_id?: string;
      service_name?: string;
    },
    getToken: () => Promise<string | null>
  ): Promise<{ clientSecret: string; paymentIntentId: string; stripeCustomerId?: string }> => {
    return apiRequest(
      `/api/customers/${id}/payments/intent`,
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Confirm a completed Stripe payment against an appointment.
   * Call after PaymentSheet / stripe.confirmPayment succeeds.
   */
  confirmAppointmentPayment: async (
    appointmentId: string,
    data: { payment_intent_id: string; amount_cents: number; tip_cents?: number },
    getToken: () => Promise<string | null>
  ): Promise<{ id: string; payment_intent_id: string; payment_status: string; amount_cents: number }> => {
    return apiRequest(
      `/api/appointments/${appointmentId}/payment`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },
};

/**
 * Orders API methods
 */
export const ordersApi = {
  /**
   * Create a new order (authenticated users)
   */
  createOrder: async (
    orderData: {
      items: Array<{
        product_id: string;
        variant_id?: string;
        quantity: number;
        unit_price: number;
        customization?: any;
      }>;
      shipping_address: any;
      billing_address: any;
      payment_intent_id?: string;
      gift_card_codes?: string[];
    },
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      '/api/orders',
      {
        method: 'POST',
        body: JSON.stringify(orderData),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Create a guest order (no auth required)
   */
  createGuestOrder: async (orderData: {
    items: Array<{
      product_id: string;
      variant_id?: string;
      quantity: number;
      unit_price: number;
      customization?: any;
    }>;
    shipping_address: any;
    billing_address: any;
    payment_intent_id?: string;
    gift_card_codes?: string[];
    guest_email: string;
  }) => {
    return apiRequest('/api/orders/guest', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  },

  /**
   * Get orders (supports keyset pagination via backend)
   */
  getOrders: async (
    params: {
      page?: number;
      limit?: number;
      status?: string;
      payment_status?: string;
      search?: string;
    } = {}, // Default empty params
    getToken: () => Promise<string | null>
  ) => {
    const query = new URLSearchParams();
    if (params.page && params.limit) {
      const offset = (params.page - 1) * params.limit;
      query.append('offset', offset.toString());
    }
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.status) query.append('status', params.status);
    if (params.payment_status) query.append('payment_status', params.payment_status);
    if (params.search) query.append('search', params.search);

    const queryString = query.toString();
    const url = `/api/orders${queryString ? `?${queryString}` : ''}`;

    return apiRequest(url, { requireAuth: true }, getToken);
  },

  /**
   * Get a specific order by ID
   */
  getOrder: async (orderId: string, getToken: () => Promise<string | null>) => {
    return apiRequest(`/api/orders/${orderId}`, { requireAuth: true }, getToken);
  },

  /**
   * Update order status (admin only)
   */
  updateOrderStatus: async (
    orderId: string,
    data: { status?: string; payment_status?: string; notes?: string },
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      `/api/orders/${orderId}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Get order by Stripe payment intent ID
   * Used by OrderConfirmation page after successful payment
   * Note: This endpoint doesn't require auth - it's public but only accessible with valid payment intent ID
   */
  getOrderByPaymentIntent: async (paymentIntentId: string) => {
    return apiRequest(`/api/orders/payment-intent/${paymentIntentId}`);
  },
};

/**
 * Wishlist API methods
 */
export const wishlistApi = {
  /**
   * Get user's wishlist
   */
  getWishlist: async (getToken: () => Promise<string | null>) => {
    return apiRequest('/api/wishlist', { requireAuth: true }, getToken);
  },

  /**
   * Add item to wishlist
   */
  addToWishlist: async (
    data: { product_id: string; variant_id?: string; notes?: string },
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      '/api/wishlist',
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Remove item from wishlist by wishlist item ID
   */
  removeFromWishlist: async (id: string, getToken: () => Promise<string | null>) => {
    return apiRequest(
      `/api/wishlist/${id}`,
      {
        method: 'DELETE',
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Add item to wishlist (simplified)
   */
  addItem: async (
    productId: string,
    variantId: string | undefined,
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      '/api/wishlist',
      {
        method: 'POST',
        body: JSON.stringify({ product_id: productId, variant_id: variantId }),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Remove item from wishlist by product ID
   */
  removeByProductId: async (productId: string, getToken: () => Promise<string | null>) => {
    return apiRequest(
      `/api/wishlist/product/${productId}`,
      {
        method: 'DELETE',
        requireAuth: true,
      },
      getToken
    );
  },
};

/**
 * Reviews API methods
 */
export const reviewsApi = {
  /**
   * Get reviews for a product
   */
  getProductReviews: async (productId: string) => {
    return apiRequest(`/api/reviews?product_id=${productId}`);
  },

  /**
   * Create a review
   */
  createReview: async (
    data: {
      product_id: string;
      order_id?: string;
      rating: number;
      title?: string;
      review_text?: string;
    },
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      '/api/reviews',
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Update a review
   */
  updateReview: async (
    id: string,
    data: { rating?: number; title?: string; review_text?: string },
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      `/api/reviews/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Mark review as helpful
   */
  markHelpful: async (id: string, getToken: () => Promise<string | null>) => {
    return apiRequest(
      `/api/reviews/${id}/helpful`,
      {
        method: 'POST',
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Get all reviews for admin moderation
   */
  getAllReviews: async (getToken: () => Promise<string | null>) => {
    return apiRequest('/api/reviews/admin', { requireAuth: true }, getToken);
  },

  /**
   * Update review visibility (admin only)
   */
  updateReviewAdmin: async (
    id: string,
    data: { is_published?: boolean },
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      `/api/reviews/${id}`,
      { method: 'PUT', body: JSON.stringify(data), requireAuth: true },
      getToken
    );
  },

  /**
   * Delete a review (admin only)
   */
  deleteReview: async (id: string, getToken: () => Promise<string | null>) => {
    return apiRequest(
      `/api/reviews/${id}`,
      { method: 'DELETE', requireAuth: true },
      getToken
    );
  },
};

/**
 * Waivers API methods
 */
export const waiversApi = {
  /**
   * Submit a waiver (anonymous or authenticated)
   */
  submitWaiver: async (
    data: {
      full_name: string;
      email: string;
      phone?: string;
      date_of_birth?: string;
      emergency_contact?: any;
      medical_conditions?: string;
      agreed_to_terms: boolean;
      signature_data?: string;
      waiver_pdf_url?: string;
      sms_consent?: boolean;
      sms_disclosure_version?: string;
      event_id?: string;
      event_guest_id?: string;
      party_guest_id?: string;
      /** Cloudflare Turnstile token — required by the API on anonymous submissions. */
      turnstile_token?: string;
    },
    getToken?: () => Promise<string | null>,
    tenantSlug?: string
  ) => {
    return apiRequest(
      '/api/waivers',
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: false,
        headers: tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : undefined,
      },
      getToken
    );
  },

  /**
   * Public prefill payload for event-guest waiver links
   * (…/waiver?event_id=<uuid>&event_guest_id=<uuid>)
   */
  getEventGuestPrefill: async (eventId: string, guestId: string) => {
    return apiRequest(`/api/events/public/${eventId}/guests/${guestId}/prefill`, {
      requireAuth: false,
    });
  },

  /**
   * Get user's waivers
   */
  getMyWaivers: async (getToken: () => Promise<string | null>) => {
    return apiRequest('/api/waivers/me', { requireAuth: true }, getToken);
  },

  /**
   * List all waivers (admin only)
   */
  listWaivers: async (
    params: { limit?: number; offset?: number; search?: string } = {},
    getToken: () => Promise<string | null>
  ) => {
    const query = new URLSearchParams();
    if (params.limit != null) query.set('limit', String(params.limit));
    if (params.offset != null) query.set('offset', String(params.offset));
    if (params.search) query.set('search', params.search);
    const queryString = query.toString();

    return apiRequest(
      `/api/waivers${queryString ? `?${queryString}` : ''}`,
      { requireAuth: true },
      getToken
    );
  },

  /**
   * Get a specific waiver (admin or owner)
   */
  getWaiver: async (id: string, getToken: () => Promise<string | null>) => {
    return apiRequest(`/api/waivers/${id}`, { requireAuth: true }, getToken);
  },

  /**
   * Resend waiver confirmation email (admin only)
   */
  resendWaiverEmail: async (id: string, getToken: () => Promise<string | null>) => {
    return apiRequest(
      `/api/waivers/${id}/resend`,
      { method: 'POST', requireAuth: true },
      getToken
    );
  },

  /**
   * Get printable waiver URL (opens in new tab)
   */
  getPrintUrl: (id: string) => `/api/waivers/${id}/print`,
};

/**
 * Onboarding checklist (tenant admin) — server-computed setup steps shared
 * with the iOS checklist.
 */
export type OnboardingStepKey = 'stripe' | 'services' | 'availability' | 'waiver' | 'team' | 'inventory';

export interface OnboardingStep {
  key: OnboardingStepKey;
  title: string;
  description: string;
  complete: boolean;
  /** Platform path, already scoped to the tenant slug (e.g. `/my-studio/settings/waivers`). */
  href: string;
}

export interface OnboardingChecklist {
  steps: OnboardingStep[];
  completed: number;
  total: number;
}

export const onboardingApi = {
  getChecklist: async (getToken: () => Promise<string | null>) => {
    return apiRequest<OnboardingChecklist>('/api/onboarding/checklist', { requireAuth: true }, getToken);
  },
};

/**
 * Waiver Templates API methods
 */
export const waiverTemplatesApi = {
  /**
   * Get tenant's active waiver template (public)
   */
  getTemplate: async (tenantSlug?: string) => {
    return apiRequest('/api/waiver-templates', {
      headers: tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : undefined,
    });
  },

  /**
   * Get the default template structure (public)
   */
  getDefault: async () => {
    return apiRequest('/api/waiver-templates/default', {});
  },

  /**
   * Create or update the tenant's waiver template (admin)
   */
  upsertTemplate: async (
    data: {
      title?: string;
      business_name?: string;
      artist_name?: string | null;
      logo_url?: string | null;
      primary_color?: string;
      secondary_color?: string;
      policy_sections?: Array<{ title: string; content: string; icon?: string; default_open?: boolean }>;
      intro_text?: string;
      footer_note?: string;
      agreement_label?: string;
      email_subject?: string;
      email_intro?: string;
      email_cta_label?: string;
      email_cta_url?: string | null;
      require_phone?: boolean;
      require_dob?: boolean;
      require_emergency_contact?: boolean;
      require_medical_conditions?: boolean;
      sms_enabled?: boolean;
      sms_message_template?: string;
    },
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest('/api/waiver-templates', {
      method: 'PUT',
      body: JSON.stringify(data),
      requireAuth: true,
    }, getToken);
  },
};

/**
 * Contact API methods
 */
export const contactApi = {
  /**
   * Submit contact form
   */
  submitContact: async (data: {
    full_name: string;
    email: string;
    phone?: string;
    subject?: string;
    message: string;
  }) => {
    return apiRequest('/api/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

/**
 * Portfolio API methods
 */
export const portfolioApi = {
  /**
   * Get all published portfolio items
   */
  getPortfolioItems: async () => {
    return apiRequest('/api/portfolio');
  },

  /**
   * Get portfolio item by slug
   */
  getPortfolioBySlug: async (slug: string) => {
    return apiRequest(`/api/portfolio/${slug}`);
  },
};

/**
 * Testimonials API methods
 */
export const testimonialsApi = {
  /**
   * Get all published testimonials (public)
   */
  getTestimonials: async () => {
    return apiRequest('/api/testimonials');
  },

  /**
   * Get all testimonials including drafts (admin only)
   */
  getAllTestimonials: async (getToken: () => Promise<string | null>) => {
    return apiRequest('/api/testimonials/admin/all', { requireAuth: true }, getToken);
  },

  /**
   * Create a testimonial (admin only)
   */
  createTestimonial: async (
    data: {
      customer_name: string;
      content: string;
      rating?: number;
      location?: string;
      is_published?: boolean;
      is_featured?: boolean;
    },
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest('/api/testimonials', { method: 'POST', body: JSON.stringify(data), requireAuth: true }, getToken);
  },

  /**
   * Update a testimonial (admin only)
   */
  updateTestimonial: async (
    id: string,
    data: Partial<{
      customer_name: string;
      content: string;
      rating: number;
      location: string;
      is_published: boolean;
      is_featured: boolean;
    }>,
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(`/api/testimonials/${id}`, { method: 'PUT', body: JSON.stringify(data), requireAuth: true }, getToken);
  },

  /**
   * Delete a testimonial (admin only)
   */
  deleteTestimonial: async (id: string, getToken: () => Promise<string | null>) => {
    return apiRequest(`/api/testimonials/${id}`, { method: 'DELETE', requireAuth: true }, getToken);
  },
};

/**
 * Categories API methods
 */
export const categoriesApi = {
  /**
   * Get all active categories (public)
   */
  getCategories: async () => {
    return apiRequest('/api/categories');
  },

  /**
   * Get category by ID or slug (public)
   */
  getCategory: async (idOrSlug: string) => {
    return apiRequest(`/api/categories/${idOrSlug}`);
  },

  /**
   * Get all categories including inactive (admin only)
   */
  getAllCategories: async (getToken: () => Promise<string | null>) => {
    return apiRequest('/api/categories/admin/all', { requireAuth: true }, getToken);
  },

  /**
   * Create a category (admin only)
   */
  createCategory: async (
    data: {
      name: string;
      slug: string;
      description?: string;
      parent_id?: string;
      image_url?: string;
      is_active?: boolean;
    },
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      '/api/categories',
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Update a category (admin only)
   */
  updateCategory: async (
    id: string,
    data: Partial<{
      name: string;
      slug: string;
      description: string;
      image_url: string;
      is_active: boolean;
      display_order: number;
    }>,
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      `/api/categories/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Delete a category (admin only)
   */
  deleteCategory: async (id: string, getToken: () => Promise<string | null>) => {
    return apiRequest(
      `/api/categories/${id}`,
      {
        method: 'DELETE',
        requireAuth: true,
      },
      getToken
    );
  },
};

/**
 * Tenants API methods
 */
export const tenantsApi = {
  /**
   * Create a new tenant/store
   */
  createTenant: async (
    payload: string | {
      name: string;
      niche?: string | null;
      city?: string | null;
      state?: string | null;
      tagline?: string | null;
      instagram?: string | null;
      accept_terms?: boolean;
      accept_privacy?: boolean;
      sms_consent?: boolean;
      sms_phone?: string | null;
      sms_disclosure_version?: string;
      /** Page the SMS box was ticked on (see sms_consent_events.page_url). */
      page_url?: string;
      /** Cloudflare Turnstile token — always required by POST /api/tenants. */
      turnstile_token?: string;
    },
    getToken: () => Promise<string | null>
  ) => {
    const body = typeof payload === 'string' ? { name: payload } : payload;
    return apiRequest(
      '/api/tenants',
      {
        method: 'POST',
        body: JSON.stringify(body),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Get my tenants
   */
  getMyTenants: async (getToken: () => Promise<string | null>) => {
    return apiRequest('/api/tenants/me', { requireAuth: true }, getToken);
  },
};

/**
 * Trial / paywall API methods
 */
export const trialApi = {
  /**
   * Get per-tenant trial status and usage counters
   */
  getStatus: async (getToken: () => Promise<string | null>) => {
    return apiRequest('/api/trial/status', { requireAuth: true }, getToken);
  },

  /**
   * Claim a demo-only resource slot (trial tenants only)
   */
  claimDemo: async (
    type: 'orders' | 'payments' | 'gift_cards' | 'waivers',
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      `/api/trial/demo/${type}`,
      {
        method: 'POST',
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Clear a demo-only resource slot
   */
  clearDemo: async (
    type: 'orders' | 'payments' | 'gift_cards' | 'waivers',
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      `/api/trial/demo/${type}`,
      {
        method: 'DELETE',
        requireAuth: true,
      },
      getToken
    );
  },
};


/**
 * Products API methods
 */
export const productsApi = {
  /**
   * Get all products.
   * Pass admin: true + getToken to include inactive products (requires admin role).
   */
  getProducts: async (
    params?: { active?: boolean; category_id?: string; admin?: boolean; external_source?: string },
    getToken?: (() => Promise<string | null>) | null
  ) => {
    const search = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        search.set(key, String(value));
      });
    }
    const qs = search.toString();
    return apiRequest(
      `/api/products${qs ? `?${qs}` : ''}`,
      params?.admin && getToken ? { requireAuth: true } : {},
      params?.admin && getToken ? getToken : undefined
    );
  },

  /**
   * Get products by category slug (public)
   */
  getProductsByCategory: async (categorySlug: string) => {
    return apiRequest(`/api/products?category_slug=${encodeURIComponent(categorySlug)}`);
  },

  /**
   * Get a single product by ID or slug (public)
   */
  getProduct: async (idOrSlug: string) => {
    return apiRequest(`/api/products/${idOrSlug}`);
  },

  /**
   * Create a new product (admin only)
   */
  createProduct: async (
    data: {
      category_id?: string;
      name: string;
      slug: string;
      description?: string;
      base_price: number;
      images?: string[];
      is_active?: boolean;
      is_featured?: boolean;
      is_storefront_published?: boolean;
      is_stripe_published?: boolean;
      pricing_type?: 'exact' | 'starting_at';
      pricing_notice?: string;
      meta_title?: string;
      meta_description?: string;
      meta_keywords?: string[];
      focus_keyword?: string;
      og_image?: string;
    },
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      '/api/products',
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Update a product (admin only)
   */
  updateProduct: async (
    id: string,
    data: Partial<{
      category_id: string;
      name: string;
      slug: string;
      description: string;
      base_price: number;
      images: string[];
      is_active: boolean;
      is_featured: boolean;
      is_storefront_published: boolean;
      is_stripe_published: boolean;
      pricing_type: 'exact' | 'starting_at';
      pricing_notice: string;
      meta_title: string;
      meta_description: string;
      meta_keywords: string[];
      focus_keyword: string;
      og_image: string;
    }>,
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      `/api/products/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Delete a product (admin only)
   */
  deleteProduct: async (id: string, getToken: () => Promise<string | null>) => {
    return apiRequest(
      `/api/products/${id}`,
      {
        method: 'DELETE',
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Create a product variant (admin only)
   */
  createVariant: async (
    productId: string,
    data: {
      name: string;
      sku?: string;
      price_modifier?: number;
      options?: Record<string, any>;
      stock_quantity?: number;
      waste_percentage?: number;
      is_available?: boolean;
    },
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      `/api/products/${productId}/variants`,
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Update a product variant (admin only)
   */
  updateVariant: async (
    productId: string,
    variantId: string,
    data: Partial<{
      name: string;
      sku: string;
      price_modifier: number;
      options: Record<string, any>;
      stock_quantity: number;
      waste_percentage: number;
      is_available: boolean;
    }>,
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      `/api/products/${productId}/variants/${variantId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Delete a product variant (admin only)
   */
  deleteVariant: async (
    productId: string,
    variantId: string,
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      `/api/products/${productId}/variants/${variantId}`,
      {
        method: 'DELETE',
        requireAuth: true,
      },
      getToken
    );
  },
};

export interface StullerProductImage {
  url: string;
  altText?: string;
}

export interface StullerProduct {
  itemNumber: string;
  name: string;
  description?: string;
  price: number;
  retailPrice?: number;
  category?: string;
  subCategory?: string;
  metal?: string;
  finish?: string;
  weight?: number;
  images?: StullerProductImage[];
  structured_attributes?: Record<string, unknown>;
  recommendations?: Array<Record<string, unknown>>;
  isAvailable: boolean;
  nextPage?: string;
}

export interface StullerDiamond {
  lotNumber: string;
  shape: string;
  caratWeight: number;
  color: string;
  clarity: string;
  price: number;
  isLabGrown: boolean;
  isAvailable: boolean;
}

export interface StullerGemstone {
  lotNumber: string;
  gemType: string;
  shape: string;
  caratWeight: number;
  price: number;
  isAvailable: boolean;
}

export const stullerApi = {
  getStatus: async (getToken: () => Promise<string | null>) => {
    return apiRequest('/api/stuller/status', { requireAuth: true }, getToken);
  },
  getProducts: async (
    params: {
      item_numbers?: string[];
      category?: string;
      metal?: string;
      page_size?: number;
      page?: string;
    } = {},
    getToken: () => Promise<string | null>
  ) => {
    const query = new URLSearchParams();
    if (params.item_numbers?.length) query.set('item_numbers', params.item_numbers.join(','));
    if (params.category) query.set('category', params.category);
    if (params.metal) query.set('metal', params.metal);
    if (params.page_size !== undefined) query.set('page_size', String(params.page_size));
    if (params.page) query.set('page', params.page);
    const qs = query.toString();
    return apiRequest<{ products: StullerProduct[]; nextPage?: string }>(
      `/api/stuller/products${qs ? `?${qs}` : ''}`,
      { requireAuth: true },
      getToken
    );
  },
  syncProducts: async (itemNumbers: string[], getToken: () => Promise<string | null>) => {
    return apiRequest(
      '/api/stuller/products/sync',
      {
        method: 'POST',
        body: JSON.stringify({ item_numbers: itemNumbers }),
        requireAuth: true,
      },
      getToken
    );
  },
  getDiamonds: async (
    params: {
      shape?: string;
      carat_min?: number;
      carat_max?: number;
      color_min?: string;
      color_max?: string;
      clarity_min?: string;
      clarity_max?: string;
      lab_grown?: boolean;
      page_size?: number;
      page?: string;
    } = {},
    getToken: () => Promise<string | null>
  ) => {
    const query = new URLSearchParams();
    if (params.shape) query.set('shape', params.shape);
    if (params.carat_min !== undefined) query.set('carat_min', String(params.carat_min));
    if (params.carat_max !== undefined) query.set('carat_max', String(params.carat_max));
    if (params.color_min) query.set('color_min', params.color_min);
    if (params.color_max) query.set('color_max', params.color_max);
    if (params.clarity_min) query.set('clarity_min', params.clarity_min);
    if (params.clarity_max) query.set('clarity_max', params.clarity_max);
    if (params.lab_grown !== undefined) query.set('lab_grown', String(params.lab_grown));
    if (params.page_size !== undefined) query.set('page_size', String(params.page_size));
    if (params.page) query.set('page', params.page);
    const qs = query.toString();
    return apiRequest<{ diamonds: StullerDiamond[]; nextPage?: string }>(
      `/api/stuller/diamonds${qs ? `?${qs}` : ''}`,
      { requireAuth: true },
      getToken
    );
  },
  getGems: async (
    params: {
      gem_type?: string;
      shape?: string;
      carat_min?: number;
      carat_max?: number;
      page_size?: number;
      page?: string;
    } = {},
    getToken: () => Promise<string | null>
  ) => {
    const query = new URLSearchParams();
    if (params.gem_type) query.set('gem_type', params.gem_type);
    if (params.shape) query.set('shape', params.shape);
    if (params.carat_min !== undefined) query.set('carat_min', String(params.carat_min));
    if (params.carat_max !== undefined) query.set('carat_max', String(params.carat_max));
    if (params.page_size !== undefined) query.set('page_size', String(params.page_size));
    if (params.page) query.set('page', params.page);
    const qs = query.toString();
    return apiRequest<{ gemstones: StullerGemstone[]; nextPage?: string }>(
      `/api/stuller/gems${qs ? `?${qs}` : ''}`,
      { requireAuth: true },
      getToken
    );
  },
  submitOrder: async (orderId: string, getToken: () => Promise<string | null>) => {
    return apiRequest(
      `/api/stuller/orders/${orderId}/submit`,
      { method: 'POST', requireAuth: true },
      getToken
    );
  },
};

/**
 * Product Collections API
 */
export interface ProductCollection {
  id: string;
  tenant_id: string;
  slug: string;
  name: string;
  description: string | null;
  external_source: string | null;
  is_visible: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  items: Array<{ product_id: string; sort_order: number }>;
}

export const productCollectionsApi = {
  list: async (
    params: { external_source?: string; visible?: boolean } = {},
    getToken?: () => Promise<string | null>
  ): Promise<ProductCollection[]> => {
    const query = new URLSearchParams();
    if (params.external_source) query.set('external_source', params.external_source);
    if (params.visible) query.set('visible', 'true');
    const qs = query.toString();
    const response = await apiRequest<ProductCollection[]>(
      `/api/product-collections${qs ? `?${qs}` : ''}`,
      {},
      getToken
    );
    return Array.isArray(response) ? response : [];
  },
  create: async (
    body: { name: string; slug?: string; description?: string; external_source?: string; is_visible?: boolean; sort_order?: number },
    getToken: () => Promise<string | null>
  ): Promise<ProductCollection> => {
    return apiRequest<ProductCollection>(
      '/api/product-collections',
      { method: 'POST', body: JSON.stringify(body), requireAuth: true },
      getToken
    );
  },
  update: async (
    id: string,
    body: Partial<{ name: string; slug: string; description: string | null; external_source: string | null; is_visible: boolean; sort_order: number }>,
    getToken: () => Promise<string | null>
  ): Promise<ProductCollection> => {
    return apiRequest<ProductCollection>(
      `/api/product-collections/${id}`,
      { method: 'PATCH', body: JSON.stringify(body), requireAuth: true },
      getToken
    );
  },
  replaceItems: async (
    id: string,
    productIds: string[],
    getToken: () => Promise<string | null>
  ): Promise<{ collection_id: string; items: Array<{ product_id: string; sort_order: number }> }> => {
    const items = productIds.map((product_id, i) => ({ product_id, sort_order: i }));
    return apiRequest(
      `/api/product-collections/${id}/items`,
      { method: 'PUT', body: JSON.stringify({ items }), requireAuth: true },
      getToken
    );
  },
  remove: async (id: string, getToken: () => Promise<string | null>): Promise<{ deleted: string }> => {
    return apiRequest(
      `/api/product-collections/${id}`,
      { method: 'DELETE', requireAuth: true },
      getToken
    );
  },
};

/**
 * Events API methods
 */
export interface Event {
  id: string;
  name: string;
  event_type: string;
  event_date: string;
  location: string;
  status: 'scheduled' | 'completed' | 'cancelled';
  guest_count: number;
  start_time: string;
  end_time: string;
  timezone: string;
  cancel_url?: string;
  reschedule_url?: string;
  created_at: string;
  notes?: string;
  invitee_email?: string;
  invitee_name?: string;
  // Payment fields (from booking system)
  payment_intent_id?: string;
  deposit_amount?: number;
  deposit_paid?: boolean;
  payment_status?: 'pending' | 'paid' | 'refunded' | 'failed';
  refund_id?: string;
  time_slot_id?: string;
  booking_source?: 'custom' | 'admin' | 'walk_in' | 'mobile_app' | 'studio_session';
}

export type EventGuestStatus =
  | 'invited'
  | 'arrived'
  | 'in_progress'
  | 'waiver_signed'
  | 'paid'
  | 'completed'
  | 'no_show';

export interface EventGuestServiceDetails {
  material?: string | null;
  style?: string | null;
  piece?: string | null;
  length_inches?: number | null;
  add_ons?: string[] | null;
  add_on_note?: string | null;
  linked_sku?: string | null;
}

export interface EventGuestRecord {
  id: string;
  tenant_id: string;
  event_id: string;
  customer_id?: string | null;
  full_name: string;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
  status: EventGuestStatus;
  waiver_id?: string | null;
  payment_intent_id?: string | null;
  payment_link_id?: string | null;
  amount_paid_cents?: number | null;
  tip_cents?: number | null;
  service_type?: string | null;
  service_notes?: string | null;
  service_details?: EventGuestServiceDetails | null;
  completed_at?: string | null;
  created_at: string;
  updated_at: string;
  customer_name?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
}

export type EventGuest = EventGuestRecord;

export interface EventQueueMetrics {
  medianCheckInToInProgressSeconds?: number | null;
  medianInProgressToPaidSeconds?: number | null;
  completionRate: number;
  noShowRate: number;
}

export interface EventQueueSummary {
  eventId: string;
  totalGuests: number;
  waitingCount: number;
  inProgressCount: number;
  completedCount: number;
  countsByStatus: Record<string, number>;
  metrics?: EventQueueMetrics | null;
  nextGuest?: EventGuestRecord | null;
}

export interface EventGuestImageRecord {
  id: string;
  filename: string;
  originalUrl?: string | null;
  previewUrl?: string | null;
  variants?: string[] | null;
}

export interface EventGuestImageUploadResponse {
  image: EventGuestImageRecord;
  purchase_image_id: string;
  event_guest_id: string;
}

export const eventsApi = {
  /**
   * Get user's events (authenticated)
   */
  getMyEvents: async (
    params: {
      status?: 'scheduled' | 'completed' | 'cancelled';
      upcoming?: boolean;
      limit?: number;
      offset?: number;
    } = {},
    getToken: () => Promise<string | null>
  ): Promise<Event[]> => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.set('status', params.status);
    if (params.upcoming) queryParams.set('upcoming', 'true');
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.offset) queryParams.set('offset', params.offset.toString());

    const queryString = queryParams.toString();
    return apiRequest(
      `/api/events${queryString ? `?${queryString}` : ''}`,
      { requireAuth: true },
      getToken
    );
  },

  /**
   * Get a specific event by ID (authenticated - user must own the event)
   */
  getEvent: async (
    eventId: string,
    getToken: () => Promise<string | null>
  ): Promise<Event> => {
    return apiRequest(`/api/events/${eventId}`, { requireAuth: true }, getToken);
  },

  /**
   * Get all events (admin only)
   */
  getAllEvents: async (
    params: {
      status?: 'scheduled' | 'completed' | 'cancelled';
      event_type?: string;
      start_date?: string;
      end_date?: string;
      /** IANA zone the bare start_date/end_date days belong to. Defaults to the browser's. */
      tz?: string;
      limit?: number;
      offset?: number;
    } = {},
    getToken: () => Promise<string | null>
  ): Promise<{ events: Event[]; pagination: { total: number; limit: number; offset: number } }> => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.set('status', params.status);
    if (params.event_type) queryParams.set('event_type', params.event_type);
    if (params.start_date) queryParams.set('start_date', params.start_date);
    if (params.end_date) queryParams.set('end_date', params.end_date);
    if (params.start_date || params.end_date) {
      // Callers build these days from local Date objects; without the zone the
      // API reads them as UTC midnight and drops evening events from the range.
      const tz = params.tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) queryParams.set('tz', tz);
    }
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.offset) queryParams.set('offset', params.offset.toString());

    const queryString = queryParams.toString();
    return apiRequest(
      `/api/events/admin/all${queryString ? `?${queryString}` : ''}`,
      { requireAuth: true },
      getToken
    );
  },

  /**
   * Create an event (admin only)
   */
  createEvent: async (
    data: Partial<Event> & {
      customer_id?: string;
      invitee_name?: string;
      invitee_email?: string;
      booking_source?: 'custom' | 'admin' | 'walk_in' | 'mobile_app' | 'studio_session';
    },
    getToken: () => Promise<string | null>
  ): Promise<Event> => {
    return apiRequest(
      '/api/events/admin',
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Get admin event detail with guests.
   */
  getAdminEvent: async (
    eventId: string,
    getToken: () => Promise<string | null>
  ): Promise<Event & { guests?: any[] }> => {
    return apiRequest(`/api/events/admin/${eventId}`, { requireAuth: true }, getToken);
  },

  /**
   * Get queue summary for an event (admin only)
   */
  getAdminEventQueue: async (
    eventId: string,
    getToken: () => Promise<string | null>
  ): Promise<EventQueueSummary> => {
    return apiRequest(`/api/events/admin/${eventId}/queue`, { requireAuth: true }, getToken);
  },

  /**
   * List guests for an event (admin only)
   */
  getEventGuests: async (
    eventId: string,
    params: {
      status?: EventGuestStatus;
      search?: string;
      limit?: number;
      offset?: number;
    } = {},
    getToken: () => Promise<string | null>
  ): Promise<{ guests: EventGuestRecord[]; pagination: { total: number; limit: number; offset: number } }> => {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.search) query.set('search', params.search);
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.offset) query.set('offset', params.offset.toString());
    const queryString = query.toString();

    return apiRequest(
      `/api/events/admin/${eventId}/guests${queryString ? `?${queryString}` : ''}`,
      { requireAuth: true },
      getToken
    );
  },

  /**
   * Update a guest on an event (admin only)
   */
  updateEventGuest: async (
    eventId: string,
    guestId: string,
    data: {
      status?: EventGuestStatus;
      notes?: string | null;
      service_type?: string | null;
      service_notes?: string | null;
      service_details?: EventGuestServiceDetails | null;
      payment_intent_id?: string | null;
      payment_link_id?: string | null;
      amount_paid_cents?: number | null;
      tip_cents?: number | null;
      waiver_id?: string | null;
      completed_at?: string | null;
    },
    getToken: () => Promise<string | null>
  ): Promise<EventGuestRecord & { lowStockAlert?: { itemId: string; itemName: string; onHand: number } }> => {
    return apiRequest(
      `/api/events/admin/${eventId}/guests/${guestId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Send a waiver link to a guest (admin only)
   */
  sendWaiverLink: async (
    eventId: string,
    guestId: string,
    getToken: () => Promise<string | null>
  ): Promise<{ ok: boolean; waiver_url: string; email_sent: boolean; sms_sent: boolean; sms_attempted: boolean }> => {
    return apiRequest(
      `/api/events/admin/${eventId}/guests/${guestId}/send-waiver-link`,
      {
        method: 'POST',
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Update an event guest status/details (admin only) — used by Studio Mode
   */
  updateGuest: async (
    eventId: string,
    guestId: string,
    data: {
      status?: string;
      notes?: string;
      service_type?: string;
      service_notes?: string;
      service_details?: Record<string, any>;
      payment_intent_id?: string;
      payment_link_id?: string;
      amount_paid_cents?: number;
      tip_cents?: number;
      waiver_id?: string;
      completed_at?: string;
    },
    getToken: () => Promise<string | null>
  ): Promise<EventGuest> => {
    return apiRequest(
      `/api/events/admin/${eventId}/guests/${guestId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Upload a guest image (admin only)
   */
  uploadGuestImage: async (
    eventId: string,
    guestId: string,
    data: { file: File; notes?: string; productName?: string },
    getToken: () => Promise<string | null>
  ): Promise<EventGuestImageUploadResponse> => {
    const token = await getToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    const formData = new FormData();
    formData.append('file', data.file);
    if (data.notes) formData.append('notes', data.notes);
    if (data.productName) formData.append('product_name', data.productName);

    const response = await fetch(`${API_BASE_URL}/api/events/admin/${eventId}/guests/${guestId}/images`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        ...(currentTenantContext.id ? { 'X-Tenant-ID': currentTenantContext.id } : {}),
        ...(currentTenantContext.slug ? { 'X-Tenant-Slug': currentTenantContext.slug } : {}),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || error.message || 'Guest image upload failed');
    }

    return response.json();
  },

  /**
   * Fetch uploaded guest images (admin only)
   */
  getGuestImages: async (
    eventId: string,
    guestId: string,
    getToken: () => Promise<string | null>
  ): Promise<{ images: Array<{ purchase_image_id: string; product_name?: string | null; notes?: string | null; created_at?: string; image_id?: string; original_url?: string | null; preview_url?: string | null; variants?: string[] | null; filename?: string | null }> }> => {
    return apiRequest(`/api/events/admin/${eventId}/guests/${guestId}/images`, { requireAuth: true }, getToken);
  },

  /**
   * Update an event (admin only)
   */
  updateEvent: async (
    eventId: string,
    data: {
      status?: 'scheduled' | 'completed' | 'cancelled';
      notes?: string;
      location?: string;
      event_date?: string;
      name?: string;
    },
    getToken: () => Promise<string | null>
  ): Promise<Event> => {
    return apiRequest(
      `/api/events/${eventId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Delete an event (admin only)
   */
  deleteEvent: async (
    eventId: string,
    getToken: () => Promise<string | null>
  ): Promise<{ success: boolean; deleted_id: string }> => {
    return apiRequest(
      `/api/events/${eventId}`,
      {
        method: 'DELETE',
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Get party guests for an event (admin only)
   * Returns guests from the party_guests table (created via party booking flow)
   */
  getPartyGuests: async (
    eventId: string,
    getToken: () => Promise<string | null>
  ): Promise<{ party_guests: PartyGuest[] }> => {
    return apiRequest(
      `/api/events/admin/${eventId}/party-guests`,
      { requireAuth: true },
      getToken
    );
  },

  /**
   * Send waiver links to all party guests with contact info who haven't signed (admin only)
   */
  sendAllWaivers: async (
    eventId: string,
    getToken: () => Promise<string | null>
  ): Promise<{ sent_count: number; guest_ids: string[] }> => {
    return apiRequest(
      `/api/events/admin/${eventId}/send-waivers`,
      {
        method: 'POST',
        requireAuth: true,
      },
      getToken
    );
  },
};

export interface PartyGuest {
  id: string;
  booking_id: string;
  event_guest_id?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  waiver_id?: string | null;
  waiver_status: 'pending' | 'sent' | 'signed';
  created_at: string;
  updated_at: string;
}

/**
 * Promotions API methods
 */
export interface PromoValidationResult {
  valid: boolean;
  error?: string;
  minimum_order?: number;
  promotion?: {
    id: string;
    code: string;
    name: string;
    description?: string;
    discount_type: 'percentage' | 'fixed_amount' | 'free_shipping';
    discount_value: number;
  };
  discount_amount?: number;
  new_total?: number;
}

export interface Promotion {
  id: string;
  code: string;
  name: string;
  description?: string;
  discount_type: 'percentage' | 'fixed_amount' | 'free_shipping';
  discount_value: number;
  minimum_order_amount: number;
  maximum_discount?: number;
  usage_limit?: number;
  usage_per_customer: number;
  starts_at?: string;
  expires_at?: string;
  first_order_only: boolean;
  is_active: boolean;
  usage_count: number;
  created_at: string;
}

/**
 * Admin API methods
 */
export interface AdminStats {
  portfolio_items: number;
  products: number;
  orders: number;
  customers: number;
  total_revenue: number;
  recent_orders: number;
  pending_orders: number;
}

export interface AdminRecentActivity {
  recent_orders: Array<{
    id: string;
    order_number: string;
    status: string;
    payment_status: string;
    total: number;
    created_at: string;
    customer_email?: string;
    customer_name?: string;
  }>;
  recent_customers: Array<{
    id: string;
    email: string;
    full_name?: string;
    created_at: string;
  }>;
}

export interface AdminSalesSummary {
  daily_sales: Array<{
    date: string;
    order_count: number;
    revenue: number;
  }>;
  status_distribution: Array<{
    status: string;
    count: number;
  }>;
  top_products: Array<{
    name: string;
    slug: string;
    total_sold: number;
    total_revenue: number;
  }>;
}

export const adminApi = {
  /**
   * Get dashboard statistics (admin only)
   */
  getStats: async (getToken: () => Promise<string | null>): Promise<AdminStats> => {
    return apiRequest('/api/admin/stats', { requireAuth: true }, getToken);
  },

  /**
   * Get recent activity (admin only)
   */
  getRecentActivity: async (getToken: () => Promise<string | null>): Promise<AdminRecentActivity> => {
    return apiRequest('/api/admin/recent-activity', { requireAuth: true }, getToken);
  },

  /**
   * Get sales summary (admin only)
   */
  getSalesSummary: async (getToken: () => Promise<string | null>): Promise<AdminSalesSummary> => {
    return apiRequest('/api/admin/sales-summary', { requireAuth: true }, getToken);
  },
};

/**
 * Promotions API methods
 */
export const promotionsApi = {
  /**
   * Validate a promo code (public - no auth required)
   */
  validateCode: async (
    data: {
      code: string;
      cart_total: number;
      cart_items?: Array<{
        productId: string;
        variantId?: string;
        price: number;
        quantity: number;
        categoryId?: string;
      }>;
      customer_id?: string;
    }
  ): Promise<PromoValidationResult> => {
    return apiRequest('/api/promotions/validate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get all promotions (admin only)
   */
  getPromotions: async (
    params: { include_inactive?: boolean } = {},
    getToken: () => Promise<string | null>
  ): Promise<Promotion[]> => {
    const queryString = params.include_inactive ? '?include_inactive=true' : '';
    return apiRequest(`/api/promotions${queryString}`, { requireAuth: true }, getToken);
  },

  /**
   * Get a single promotion (admin only)
   */
  getPromotion: async (
    id: string,
    getToken: () => Promise<string | null>
  ): Promise<Promotion & { recent_usage: any[] }> => {
    return apiRequest(`/api/promotions/${id}`, { requireAuth: true }, getToken);
  },

  /**
   * Create a new promotion (admin only)
   */
  createPromotion: async (
    data: {
      code: string;
      name: string;
      description?: string;
      discount_type: 'percentage' | 'fixed_amount' | 'free_shipping';
      discount_value: number;
      minimum_order_amount?: number;
      maximum_discount?: number;
      usage_limit?: number;
      usage_per_customer?: number;
      starts_at?: string;
      expires_at?: string;
      applicable_products?: string[];
      applicable_categories?: string[];
      first_order_only?: boolean;
      is_active?: boolean;
    },
    getToken: () => Promise<string | null>
  ): Promise<Promotion> => {
    return apiRequest(
      '/api/promotions',
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Update a promotion (admin only)
   */
  updatePromotion: async (
    id: string,
    data: Partial<{
      code: string;
      name: string;
      description: string;
      discount_type: 'percentage' | 'fixed_amount' | 'free_shipping';
      discount_value: number;
      minimum_order_amount: number;
      maximum_discount: number;
      usage_limit: number;
      usage_per_customer: number;
      starts_at: string;
      expires_at: string;
      applicable_products: string[];
      applicable_categories: string[];
      first_order_only: boolean;
      is_active: boolean;
    }>,
    getToken: () => Promise<string | null>
  ): Promise<Promotion> => {
    return apiRequest(
      `/api/promotions/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Delete a promotion (admin only)
   */
  deletePromotion: async (
    id: string,
    getToken: () => Promise<string | null>
  ): Promise<{ success: boolean; id: string }> => {
    return apiRequest(
      `/api/promotions/${id}`,
      {
        method: 'DELETE',
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Record promotion usage (called when order is created)
   */
  recordUsage: async (
    promotionId: string,
    data: { order_id?: string; discount_amount: number },
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      `/api/promotions/${promotionId}/usage`,
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },
};

/**
 * Gift Card types
 */
export interface GiftCard {
  id: string;
  code: string;
  initial_balance: number;
  current_balance: number;
  recipient_email?: string;
  recipient_name?: string;
  purchaser_email?: string;
  purchaser_name?: string;
  message?: string;
  status: 'active' | 'used' | 'expired' | 'cancelled';
  expires_at?: string;
  created_at: string;
  updated_at: string;
  transaction_count?: number;
  stripe_coupon_id?: string | null;
  stripe_promotion_code_id?: string | null;
}

export interface GiftCardTransaction {
  id: string;
  gift_card_id: string;
  transaction_type: 'credit' | 'debit' | 'redemption';
  amount: number;
  balance_before: number;
  balance_after: number;
  order_id?: string;
  notes?: string;
  created_at: string;
}

/**
 * Gift Cards API methods
 */
export const giftCardsApi = {
  /**
   * Get all gift cards (admin only)
   */
  getGiftCards: async (
    params: { status?: string; search?: string; limit?: number; offset?: number } = {},
    getToken: () => Promise<string | null>
  ): Promise<{ gift_cards: GiftCard[]; total: number; limit: number; offset: number }> => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.set('status', params.status);
    if (params.search) queryParams.set('search', params.search);
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.offset) queryParams.set('offset', String(params.offset));
    const queryString = queryParams.toString();
    return apiRequest(
      `/api/gift-cards${queryString ? `?${queryString}` : ''}`,
      { requireAuth: true },
      getToken
    );
  },

  /**
   * Get single gift card with transactions (admin only)
   */
  getGiftCard: async (
    id: string,
    getToken: () => Promise<string | null>
  ): Promise<GiftCard & { transactions: GiftCardTransaction[] }> => {
    return apiRequest(`/api/gift-cards/${id}`, { requireAuth: true }, getToken);
  },

  /**
   * Verify Stripe coupon + promotion code exist (admin only)
   */
  verifyStripe: async (
    id: string,
    getToken: () => Promise<string | null>
  ): Promise<{
    synced: boolean;
    stripe_coupon_id: string | null;
    stripe_promotion_code_id: string | null;
    coupon_exists: boolean;
    promotion_code_exists: boolean;
  }> => {
    return apiRequest(`/api/gift-cards/${id}/stripe`, { requireAuth: true }, getToken);
  },

  /**
   * Create or reconcile Stripe coupon + promotion code for an existing gift card (admin only)
   */
  createStripe: async (
    id: string,
    getToken: () => Promise<string | null>
  ): Promise<{ message: string; gift_card?: GiftCard }> => {
    return apiRequest(`/api/gift-cards/${id}/stripe-create`, { method: 'POST', requireAuth: true }, getToken);
  },

  /**
   * Validate gift card code (public)
   */
  validateCode: async (
    code: string
  ): Promise<{ valid: boolean; balance?: number; code?: string; message?: string }> => {
    return apiRequest(`/api/gift-cards/code/${code}`);
  },

  /**
   * Create gift card (admin only)
   */
  createGiftCard: async (
    data: {
      initial_balance: number;
      recipient_email?: string;
      recipient_name?: string;
      purchaser_email?: string;
      purchaser_name?: string;
      message?: string;
      expires_at?: string;
      allow_without_stripe?: boolean;
    },
    getToken: () => Promise<string | null>
  ): Promise<GiftCard> => {
    return apiRequest(
      '/api/gift-cards',
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Update gift card (admin only)
   */
  updateGiftCard: async (
    id: string,
    data: Partial<GiftCard>,
    getToken: () => Promise<string | null>
  ): Promise<GiftCard> => {
    return apiRequest(
      `/api/gift-cards/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Deactivate gift card (admin only)
   */
  deactivateGiftCard: async (
    id: string,
    getToken: () => Promise<string | null>
  ): Promise<{ message: string; gift_card: GiftCard }> => {
    return apiRequest(
      `/api/gift-cards/${id}`,
      {
        method: 'DELETE',
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Adjust gift card balance (admin only)
   */
  adjustBalance: async (
    id: string,
    data: { amount: number; reason?: string },
    getToken: () => Promise<string | null>
  ): Promise<{ message: string; previous_balance: number; adjustment: number; new_balance: number }> => {
    return apiRequest(
      `/api/gift-cards/${id}/adjust`,
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Redeem a gift card at POS (admin only)
   */
  redeemGiftCard: async (
    data: { code: string; amount: number; order_id?: string; description?: string },
    getToken: () => Promise<string | null>
  ): Promise<{ success: boolean; new_balance: number; amount_redeemed: number }> => {
    return apiRequest('/api/gift-cards/redeem', {
      method: 'POST',
      body: JSON.stringify(data),
      requireAuth: true,
    }, getToken);
  },
};

// ============================================
// EXTERNAL PAYMENTS API
// ============================================

/**
 * External Payments API methods
 */
export const externalPaymentsApi = {
  recordPayment: async (
    data: {
      method: string;
      amount_cents: number;
      description?: string;
      customer_id?: string;
      booking_id?: string;
      event_guest_id?: string;
      reference?: string;
    },
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest('/api/payments/external', {
      method: 'POST',
      body: JSON.stringify(data),
      requireAuth: true,
    }, getToken);
  },

  getPayments: async (
    params: { customer_id?: string; limit?: number; offset?: number } = {},
    getToken: () => Promise<string | null>
  ) => {
    const query = new URLSearchParams();
    if (params.customer_id) query.set('customer_id', params.customer_id);
    if (params.limit) query.set('limit', String(params.limit));
    if (params.offset) query.set('offset', String(params.offset));
    const qs = query.toString();
    return apiRequest(`/api/payments/external${qs ? `?${qs}` : ''}`, { requireAuth: true }, getToken);
  },
};

// ============================================
// TRANSACTIONS API
// ============================================

/**
 * Transaction record returned from the unified transactions endpoint
 */
export interface Transaction {
  id: string;
  source: string;
  method: string;
  amount_cents: number;
  currency: string;
  status: string;
  customer_id?: string;
  customer_name?: string;
  booking_id?: string;
  reference?: string;
  description?: string;
  created_at: string;
  date?: string;
  amount?: number;
}

/**
 * Unified Transactions API methods
 */
export const transactionsApi = {
  getTransactions: async (
    params: {
      customer_id?: string;
      date_from?: string;
      date_to?: string;
      method?: string;
      status?: string;
      limit?: number;
      offset?: number;
    } = {},
    getToken: () => Promise<string | null>
  ): Promise<{ transactions: Transaction[]; total: number; limit: number; offset: number }> => {
    const query = new URLSearchParams();
    if (params.customer_id) query.set('customer_id', params.customer_id);
    if (params.date_from) query.set('date_from', params.date_from);
    if (params.date_to) query.set('date_to', params.date_to);
    if (params.method) query.set('method', params.method);
    if (params.status) query.set('status', params.status);
    if (params.limit) query.set('limit', String(params.limit));
    if (params.offset) query.set('offset', String(params.offset));
    const qs = query.toString();
    return apiRequest(`/api/payments/transactions${qs ? `?${qs}` : ''}`, { requireAuth: true }, getToken);
  },
};

// ============================================
// AVAILABILITY API
// ============================================

/**
 * Availability types
 */
export interface AvailabilityRule {
  id: string;
  rule_type: 'business_hours' | 'blackout' | 'special' | 'override' | 'party_window';
  name: string;
  description?: string;
  day_of_week?: number;
  specific_date?: string;
  start_time: string;
  end_time: string;
  is_available: boolean;
  slot_capacity: number;
  slot_duration_minutes: number;
  buffer_minutes: number;
  event_types?: string[];
  priority: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TimeSlot {
  id: string;
  slot_date: string;
  start_time: string;
  end_time: string;
  slot_type: 'individual' | 'party';
  capacity: number;
  booked_count: number;
  is_available: boolean;
  is_blocked: boolean;
  block_reason?: string;
}

export interface BookingSettings {
  lead_time_hours: number;
  max_advance_days: number;
  default_slot_duration_individual: number;
  default_slot_duration_party: number;
  default_buffer_minutes: number;
  individual_deposit_amount: number;
  party_deposit_amount: number;
  party_minimum_guests: number;
  cancellation_window_hours: number;
  timezone: string;
}

export interface AvailableDates {
  available_dates: Array<{ slot_date: string; available_slots: number }>;
  business_hours: Array<{ day_of_week: number; start_time: string; end_time: string }>;
  blackout_dates: string[];
}

/**
 * Availability API methods
 */
export const availabilityApi = {
  /**
   * Get available time slots for a date range
   */
  getSlots: async (params: {
    start_date: string;
    end_date?: string;
    slot_type?: 'individual' | 'party';
  }): Promise<{
    slots: TimeSlot[];
    generated: boolean;
    settings: { lead_time_hours: number; max_advance_days: number; timezone: string };
  }> => {
    const queryParams = new URLSearchParams();
    queryParams.set('start_date', params.start_date);
    if (params.end_date) queryParams.set('end_date', params.end_date);
    if (params.slot_type) queryParams.set('slot_type', params.slot_type);

    return apiRequest(`/api/availability/slots?${queryParams.toString()}`);
  },

  /**
   * Get dates with availability (for calendar UI)
   */
  getDates: async (params: {
    start_date: string;
    end_date: string;
    slot_type?: 'individual' | 'party';
  }): Promise<AvailableDates> => {
    const queryParams = new URLSearchParams();
    queryParams.set('start_date', params.start_date);
    queryParams.set('end_date', params.end_date);
    if (params.slot_type) queryParams.set('slot_type', params.slot_type);

    return apiRequest(`/api/availability/dates?${queryParams.toString()}`);
  },

  /**
   * Get booking settings
   */
  getSettings: async (): Promise<BookingSettings> => {
    return apiRequest('/api/availability/settings');
  },

  // Admin methods

  /**
   * Get all availability rules (admin)
   */
  getRules: async (
    params: { rule_type?: string; active_only?: boolean },
    getToken: () => Promise<string | null>
  ): Promise<{ rules: AvailabilityRule[] }> => {
    const queryParams = new URLSearchParams();
    if (params.rule_type) queryParams.set('rule_type', params.rule_type);
    if (params.active_only) queryParams.set('active_only', 'true');

    return apiRequest(
      `/api/availability/rules?${queryParams.toString()}`,
      { requireAuth: true },
      getToken
    );
  },

  /**
   * Create availability rule (admin)
   */
  createRule: async (
    data: Partial<AvailabilityRule>,
    getToken: () => Promise<string | null>
  ): Promise<{ rule: AvailabilityRule }> => {
    return apiRequest(
      '/api/availability/rules',
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Update availability rule (admin)
   */
  updateRule: async (
    id: string,
    data: Partial<AvailabilityRule>,
    getToken: () => Promise<string | null>
  ): Promise<{ rule: AvailabilityRule }> => {
    return apiRequest(
      `/api/availability/rules/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Delete availability rule (admin)
   */
  deleteRule: async (
    id: string,
    getToken: () => Promise<string | null>
  ): Promise<{ success: boolean; deleted_id: string }> => {
    return apiRequest(
      `/api/availability/rules/${id}`,
      {
        method: 'DELETE',
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Generate time slots (admin)
   */
  generateSlots: async (
    data: { start_date: string; end_date: string; slot_type?: string },
    getToken: () => Promise<string | null>
  ): Promise<{ success: boolean; generated: number; inserted: number }> => {
    return apiRequest(
      '/api/availability/slots/generate',
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Get admin slots view (admin)
   */
  getAdminSlots: async (
    params: {
      start_date?: string;
      end_date?: string;
      slot_type?: string;
      include_blocked?: boolean;
    },
    getToken: () => Promise<string | null>
  ): Promise<{ slots: TimeSlot[] }> => {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.set('start_date', params.start_date);
    if (params.end_date) queryParams.set('end_date', params.end_date);
    if (params.slot_type) queryParams.set('slot_type', params.slot_type);
    if (params.include_blocked) queryParams.set('include_blocked', 'true');

    return apiRequest(
      `/api/availability/slots/admin?${queryParams.toString()}`,
      { requireAuth: true },
      getToken
    );
  },

  /**
   * Update time slot (admin)
   */
  updateSlot: async (
    id: string,
    data: { is_blocked?: boolean; block_reason?: string; is_available?: boolean; capacity?: number },
    getToken: () => Promise<string | null>
  ): Promise<{ slot: TimeSlot }> => {
    return apiRequest(
      `/api/availability/slots/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Get all booking settings (admin)
   */
  getAdminSettings: async (
    getToken: () => Promise<string | null>
  ): Promise<{ settings: Array<{ setting_key: string; setting_value: string; setting_type: string; description?: string }> }> => {
    return apiRequest(
      '/api/availability/settings/admin',
      { requireAuth: true },
      getToken
    );
  },

  /**
   * Update booking setting (admin)
   */
  updateSetting: async (
    key: string,
    data: { value: string | number | boolean; description?: string },
    getToken: () => Promise<string | null>
  ): Promise<{ setting: { setting_key: string; setting_value: string } }> => {
    return apiRequest(
      `/api/availability/settings/${key}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },
};

// ============================================
// BOOKING API
// ============================================

/**
 * Booking types
 */
export interface BookingRequest {
  slot_id?: string;
  slot_date?: string;
  start_time?: string;
  end_time?: string;
  event_type: 'individual' | 'party';
  guest_count?: number;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  notes?: string;
  location_preference?: 'studio' | 'mobile';
  special_requests?: string;
  /** Cloudflare Turnstile token — required by the API on anonymous submissions. */
  turnstile_token?: string;
}

export interface BookingValidation {
  valid: boolean;
  errors: string[];
  warnings: string[];
  slot?: TimeSlot;
  deposit_amount?: number;
}

export interface PaymentIntentResponse {
  client_secret: string;
  payment_intent_id: string;
  event_id: string;
  deposit_amount: number;
  deposit_amount_display: string;
}

export interface BookingConfirmation {
  success: boolean;
  payment_required?: boolean;
  payment_notice?: string;
  event: {
    id: string;
    name: string;
    event_type: string;
    start_time: string;
    end_time: string;
    location: string;
    guest_count: number;
    status?: string;
    payment_status?: string | null;
  };
}

export interface BookingRescheduleResult {
  success: boolean;
  rescheduled: boolean;
  new_start_time: string;
  new_end_time: string;
  event: {
    id: string;
    name: string;
    event_type: string;
    start_time: string;
    end_time: string;
    location: string;
    guest_count: number;
    status?: string;
    payment_status?: string | null;
  };
}

export interface CancellationResult {
  success: boolean;
  cancelled: boolean;
  refund_eligible: boolean;
  refund_processed: boolean;
  refund_id?: string;
  message: string;
}

export interface BookingWaitlistEntry {
  id: string;
  tenant_id: string;
  customer_id?: string | null;
  converted_event_id?: string | null;
  event_type: 'individual' | 'party';
  customer_name: string;
  customer_email?: string | null;
  customer_phone?: string | null;
  preferred_date?: string | null;
  preferred_start_time?: string | null;
  preferred_end_time?: string | null;
  party_size: number;
  source: 'web' | 'ios' | 'admin';
  status: 'pending' | 'notified' | 'converted' | 'cancelled';
  notes?: string | null;
  notified_at?: string | null;
  converted_at?: string | null;
  created_at: string;
  updated_at?: string | null;
}


export interface PaymentCapability {
  tenant_id: string;
  tenant_slug: string;
  payments_enabled: boolean;
  booking_deposits_enabled: boolean;
  requires_stripe_connect: boolean;
}

export const paymentCapabilitiesApi = {
  getTenant: async (tenantSlug: string): Promise<PaymentCapability> => {
    return apiRequest(`/api/public/tenant/${encodeURIComponent(tenantSlug)}/payment-capabilities`);
  },
};

/**
 * Booking API methods
 */
export const bookingApi = {
  /**
   * Validate a booking request
   */
  validate: async (data: BookingRequest): Promise<BookingValidation> => {
    return apiRequest('/api/booking/validate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Create payment intent for booking deposit
   */
  createPaymentIntent: async (data: BookingRequest): Promise<PaymentIntentResponse> => {
    return apiRequest('/api/booking/create-intent', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Create a booking without a deposit when online payments are disabled.
   */
  createUnpaidBooking: async (data: BookingRequest): Promise<BookingConfirmation> => {
    return apiRequest('/api/booking/create-unpaid', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Confirm booking after successful payment
   */
  confirmBooking: async (data: {
    payment_intent_id: string;
    event_id: string;
    sms_consent?: boolean;
    sms_disclosure_version?: string;
  }): Promise<BookingConfirmation> => {
    return apiRequest('/api/booking/confirm', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Cancel a booking
   */
  cancelBooking: async (
    data: { event_id: string; reason?: string },
    getToken: () => Promise<string | null>
  ): Promise<CancellationResult> => {
    return apiRequest(
      '/api/booking/cancel',
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Reschedule a booking
   */
  rescheduleBooking: async (
    data: {
      event_id: string;
      new_slot_id?: string;
      new_start_time?: string;
      new_end_time?: string;
      new_slot_date?: string;
    },
    getToken: () => Promise<string | null>
  ): Promise<BookingRescheduleResult> => {
    return apiRequest(
      '/api/booking/reschedule',
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  joinWaitlist: async (data: {
    event_type: 'individual' | 'party';
    customer_name: string;
    customer_email?: string;
    customer_phone?: string;
    preferred_date?: string;
    preferred_start_time?: string;
    preferred_end_time?: string;
    party_size?: number;
    notes?: string;
    source?: 'web' | 'ios' | 'admin';
    /** Cloudflare Turnstile token — required by the API on anonymous submissions. */
    turnstile_token?: string;
  }): Promise<{ success: boolean; waitlist_entry: BookingWaitlistEntry }> => {
    return apiRequest('/api/booking/waitlist', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getWaitlist: async (
    params: { status?: string; event_type?: string } = {},
    getToken: () => Promise<string | null>
  ): Promise<{ waitlist: BookingWaitlistEntry[] }> => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.set('status', params.status);
    if (params.event_type) queryParams.set('event_type', params.event_type);

    return apiRequest(
      `/api/booking/waitlist${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
      { requireAuth: true },
      getToken
    );
  },

  notifyWaitlist: async (
    id: string,
    getToken: () => Promise<string | null>
  ): Promise<{ success: boolean; waitlist_entry: BookingWaitlistEntry }> => {
    return apiRequest(
      `/api/booking/waitlist/${id}/notify`,
      {
        method: 'POST',
        body: JSON.stringify({}),
        requireAuth: true,
      },
      getToken
    );
  },

  convertWaitlist: async (
    id: string,
    eventId: string,
    getToken: () => Promise<string | null>
  ): Promise<{ success: boolean; waitlist_entry: BookingWaitlistEntry }> => {
    return apiRequest(
      `/api/booking/waitlist/${id}/convert`,
      {
        method: 'POST',
        body: JSON.stringify({ event_id: eventId }),
        requireAuth: true,
      },
      getToken
    );
  },

  cancelWaitlist: async (
    id: string,
    getToken: () => Promise<string | null>
  ): Promise<{ success: boolean; waitlist_entry: BookingWaitlistEntry }> => {
    return apiRequest(
      `/api/booking/waitlist/${id}/cancel`,
      {
        method: 'POST',
        body: JSON.stringify({}),
        requireAuth: true,
      },
      getToken
    );
  },
};


/**
 * Inventory API methods
 */
export const inventoryApi = {
  metadata: async (getToken?: () => Promise<string | null>) => {
    return apiRequest('/api/inventory/metadata', { requireAuth: true }, getToken);
  },

  list: async (params: { type?: string; material?: string; low_stock?: boolean } = {}, getToken?: () => Promise<string | null>) => {
    const query = new URLSearchParams();
    if (params.type) query.set('type', params.type);
    if (params.material) query.set('material', params.material);
    if (params.low_stock) query.set('low_stock', 'true');
    const queryString = query.toString();

    return apiRequest<{ items: any[] }>(
      `/api/inventory${queryString ? `?${queryString}` : ''}`,
      { requireAuth: true },
      getToken
    );
  },

  create: async (data: any, getToken: () => Promise<string | null>) => {
    return apiRequest(
      '/api/inventory', {
      method: 'POST',
      body: JSON.stringify(data),
      requireAuth: true
    }, getToken);
  },

  get: async (id: string, getToken: () => Promise<string | null>) => {
    return apiRequest(`/api/inventory/${id}`, { requireAuth: true }, getToken);
  },

  update: async (id: string, data: any, getToken: () => Promise<string | null>) => {
    return apiRequest(`/api/inventory/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      requireAuth: true
    }, getToken);
  },

  createTransaction: async (id: string, data: { type: string; quantity_change: number; unit_cost?: number; notes?: string; reference_id?: string; session_context?: string }, getToken: () => Promise<string | null>) => {
    return apiRequest(`/api/inventory/${id}/transactions`, {
      method: 'POST',
      body: JSON.stringify(data),
      requireAuth: true
    }, getToken);
  },

  getTransactions: async (id: string, params: { limit?: number; offset?: number } = {}, getToken: () => Promise<string | null>) => {
    const query = new URLSearchParams();
    if (params.limit) query.set('limit', String(params.limit));
    if (params.offset) query.set('offset', String(params.offset));
    const qs = query.toString();
    return apiRequest<{ transactions: any[]; total: number; limit: number; offset: number }>(
      `/api/inventory/${id}/transactions${qs ? `?${qs}` : ''}`,
      { requireAuth: true },
      getToken
    );
  },

  receive: async (id: string, data: { quantity: number; unit_cost?: number; supplier?: string; po_number?: string; notes?: string; session_context?: string }, getToken: () => Promise<string | null>) => {
    return apiRequest(`/api/inventory/${id}/receive`, {
      method: 'POST',
      body: JSON.stringify(data),
      requireAuth: true
    }, getToken);
  },

  getDashboardStats: async (getToken: () => Promise<string | null>) => {
    return apiRequest('/api/inventory/dashboard/stats', { requireAuth: true }, getToken);
  },

  bulkCreate: async (items: any[], getToken: () => Promise<string | null>) => {
    return apiRequest('/api/inventory/bulk', {
      method: 'POST',
      body: JSON.stringify({ items }),
      requireAuth: true
    }, getToken);
  },

  bulkUpdate: async (items: any[], getToken: () => Promise<string | null>) => {
    return apiRequest('/api/inventory/bulk', {
      method: 'PUT',
      body: JSON.stringify({ items }),
      requireAuth: true
    }, getToken);
  },

  csvImport: async (rows: any[], getToken: () => Promise<string | null>) => {
    return apiRequest('/api/inventory/csv-import', {
      method: 'POST',
      body: JSON.stringify({ rows }),
      requireAuth: true
    }, getToken);
  },

  getCSVTemplate: async (getToken: () => Promise<string | null>) => {
    const token = await getToken();
    if (!token) throw new Error('Authentication required');

    const response = await fetch(`${API_BASE_URL}/api/inventory/csv-template`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        ...(currentTenantContext.id ? { 'X-Tenant-ID': currentTenantContext.id } : {}),
        ...(currentTenantContext.slug ? { 'X-Tenant-Slug': currentTenantContext.slug } : {}),
      },
    });
    if (!response.ok) throw new Error('Failed to fetch template');
    return response.text();
  },

  syncAvailability: async (getToken: () => Promise<string | null>) => {
    return apiRequest('/api/inventory/sync-availability', {
      method: 'POST',
      requireAuth: true
    }, getToken);
  },

  syncFromSale: async (orderId: string, getToken: () => Promise<string | null>) => {
    return apiRequest('/api/inventory/sync-from-sale', {
      method: 'POST',
      body: JSON.stringify({ order_id: orderId }),
      requireAuth: true
    }, getToken);
  },

  getLinkedVariants: async (id: string, getToken: () => Promise<string | null>) => {
    return apiRequest(`/api/inventory/${id}/linked-variants`, { requireAuth: true }, getToken);
  },

  getRestockRecommendations: async (
    style: 'conservative' | 'balanced' | 'aggressive' = 'balanced',
    getToken: () => Promise<string | null>
  ) => {
    return apiRequest(
      `/api/inventory/restock-recommendations?style=${style}`,
      { requireAuth: true },
      getToken
    );
  },

  // ── Snapshots ──────────────────────────────────────────────────────────────

  captureSnapshot: async (date: string | null, getToken: () => Promise<string | null>) => {
    return apiRequest('/api/inventory/snapshots/capture', {
      method: 'POST',
      body: JSON.stringify(date ? { date } : {}),
      requireAuth: true,
    }, getToken);
  },

  getSnapshots: async (
    params: { days?: number; item_id?: string; group_by?: 'item' | 'date' } = {},
    getToken: () => Promise<string | null>
  ) => {
    const query = new URLSearchParams();
    if (params.days)     query.set('days',      String(params.days));
    if (params.item_id)  query.set('item_id',   params.item_id);
    if (params.group_by) query.set('group_by',  params.group_by);
    const qs = query.toString();
    return apiRequest(
      `/api/inventory/snapshots${qs ? `?${qs}` : ''}`,
      { requireAuth: true },
      getToken
    );
  },

  // ── Purchase Order generation ─────────────────────────────────────────────

  generatePurchaseOrder: async (
    params: { style?: string; window_days?: number; lead_time_days?: number } = {},
    getToken: () => Promise<string | null>
  ) => {
    const query = new URLSearchParams();
    if (params.style)           query.set('style',          params.style);
    if (params.window_days)     query.set('window_days',    String(params.window_days));
    if (params.lead_time_days)  query.set('lead_time_days', String(params.lead_time_days));
    const qs = query.toString();
    return apiRequest(
      `/api/inventory/purchase-order${qs ? `?${qs}` : ''}`,
      { requireAuth: true },
      getToken
    );
  },
};

export interface PnlReport {
  start: string | null;
  end: string | null;
  revenue: number;
  cogs: number;
  gross_profit: number;
  gross_margin?: number;
  order_count?: number;
}

export const reportsApi = {
  getPnl: async (
    params: { start?: string; end?: string } = {},
    getToken: () => Promise<string | null>
  ): Promise<PnlReport> => {
    const query = new URLSearchParams();
    if (params.start) query.set('start', params.start);
    if (params.end) query.set('end', params.end);
    const queryString = query.toString();

    return apiRequest(
      `/api/reports/pnl${queryString ? `?${queryString}` : ''}`,
      { requireAuth: true },
      getToken
    );
  },

  getMarginsByMaterial: async (
    params: { start?: string; end?: string } = {},
    getToken: () => Promise<string | null>
  ) => {
    const query = new URLSearchParams();
    if (params.start) query.set('start', params.start);
    if (params.end) query.set('end', params.end);
    const queryString = query.toString();

    return apiRequest(
      `/api/reports/margins-by-material${queryString ? `?${queryString}` : ''}`,
      { requireAuth: true },
      getToken
    );
  },

  getTopItems: async (
    params: { start?: string; end?: string; limit?: number } = {},
    getToken: () => Promise<string | null>
  ) => {
    const query = new URLSearchParams();
    if (params.start) query.set('start', params.start);
    if (params.end) query.set('end', params.end);
    if (params.limit) query.set('limit', String(params.limit));
    const queryString = query.toString();

    return apiRequest(
      `/api/reports/top-items${queryString ? `?${queryString}` : ''}`,
      { requireAuth: true },
      getToken
    );
  },
};

// ============================================
// UNIVERSAL SEARCH API
// ============================================

export interface UniversalSearchResult {
  entity_id: string;
  entity_type: 'product' | 'event' | 'customer' | 'order';
  title: string;
  subtitle: string;
  extra: string;
  score: number;
}

export interface UniversalSearchResponse {
  results: UniversalSearchResult[];
  query: string;
  total: number;
}

export const searchApi = {
  /**
   * Universal search across products, events, customers, and orders
   */
  search: async (
    params: {
      q: string;
      types?: string[];
      limit?: number;
    },
    getToken: () => Promise<string | null>
  ): Promise<UniversalSearchResponse> => {
    const queryParams = new URLSearchParams();
    queryParams.set('q', params.q);
    if (params.types && params.types.length > 0) {
      queryParams.set('types', params.types.join(','));
    }
    if (params.limit) queryParams.set('limit', String(params.limit));

    return apiRequest(
      `/api/search?${queryParams.toString()}`,
      { requireAuth: true },
      getToken
    );
  },

  /**
   * Log a click-through event for search analytics
   */
  logClick: async (
    params: { query: string; entityType: string; entityId: string },
    getToken: () => Promise<string | null>
  ): Promise<void> => {
    try {
      await apiRequest(
        '/api/search/click',
        {
          method: 'POST',
          body: JSON.stringify(params),
          requireAuth: true,
        },
        getToken
      );
    } catch {
      // Fire-and-forget — never block UI for analytics
    }
  },
};

// ============================================
// AI ASSISTANT API
// ============================================

export interface AssistantMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface AssistantChatResponse {
  sessionId: string;
  message: string;
  toolResults: Array<{
    toolCallId: string;
    toolName: string;
    result: Record<string, unknown>;
    error?: string;
  }>;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

export interface AssistantSession {
  sessionId: string;
  tenantId: string;
  userId: string;
  surface: 'platform' | 'ios' | 'marketing';
  messages: AssistantMessage[];
  createdAt: string;
  updatedAt: string;
}

export const assistantApi = {
  /**
   * Send a message to the AI assistant.
   */
  chat: async (
    data: { message: string; surface: 'platform' | 'ios' | 'marketing'; sessionId?: string },
    getToken: () => Promise<string | null>
  ): Promise<AssistantChatResponse> => {
    return apiRequest(
      '/api/ai/assistant/chat',
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: true,
      },
      getToken
    );
  },

  /**
   * Retrieve a session's message history.
   */
  getSession: async (
    sessionId: string,
    getToken: () => Promise<string | null>
  ): Promise<AssistantSession> => {
    return apiRequest(
      `/api/ai/assistant/sessions/${encodeURIComponent(sessionId)}`,
      { requireAuth: true },
      getToken
    );
  },
};
